import AppsIcon from '@mui/icons-material/Apps';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';

import { isHrRole, isMarketingRole, isSuperAdmin } from './roles';

/** Sidebar zones — presentational grouping only; RBAC filtering is unchanged. */
export type NavGroup = 'WORKSPACE' | 'RECORDS' | 'OUTREACH' | 'ADMINISTRATION';

/** Render order of the sidebar zones. */
export const NAV_GROUP_ORDER: NavGroup[] = [
  'WORKSPACE',
  'RECORDS',
  'OUTREACH',
  'ADMINISTRATION',
];

interface NavigationItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  /** Presentational sidebar zone. */
  group: NavGroup;
  /** Hidden from dedicated-HR users (Super-Admin oversight/admin/finance areas). */
  hideForHr?: boolean;
  /** Shown only to dedicated-HR users (their assigned-account work); hidden from Super Admins. */
  hrOnly?: boolean;
  /** Optional small role tag shown after the label (e.g. "HR"). */
  roleTag?: string;
}

const NavigationItems: NavigationItem[] = [
  // Dashboard now includes analytics as tabs (Overview + Hiring Funnel, Pipeline, etc.),
  // so there is no separate Analytics entry. Data is scoped server-side (an HR sees only
  // their assigned clients; a Super Admin sees platform-wide).
  { id: '1',  icon: <DashboardOutlinedIcon />,      label: 'Dashboard',         path: '/dashboard',        group: 'WORKSPACE' },
  { id: '1b', icon: <HomeWorkOutlinedIcon />,       label: 'Premium Hiring Clients', path: '/my-accounts',      group: 'WORKSPACE', hrOnly: true, roleTag: 'HR' },
  { id: '14', icon: <NotificationsOutlinedIcon />,  label: 'Notifications',     path: '/notifications',    group: 'WORKSPACE' },
  { id: '15', icon: <GroupAddOutlinedIcon />,       label: 'Referrals',         path: '/referrals',        group: 'WORKSPACE' },
  { id: '15b', icon: <HandshakeOutlinedIcon />,     label: 'HR Ambassadors',    path: '/ambassadors',      group: 'WORKSPACE', hideForHr: true },
  { id: '2',  icon: <WorkOutlineOutlinedIcon />,    label: 'Jobs',              path: '/jobs',             group: 'RECORDS' },
  { id: '3',  icon: <CorporateFareOutlinedIcon />,  label: 'Companies',         path: '/companies',        group: 'RECORDS' },
  { id: '4',  icon: <Groups2OutlinedIcon />,        label: 'Job Seekers',       path: '/jobseekers',       group: 'RECORDS' },
  { id: '5',  icon: <RecentActorsOutlinedIcon />,   label: 'Employers',         path: '/employers',        group: 'RECORDS' },
  { id: '5b', icon: <SupervisorAccountOutlinedIcon />, label: 'HR',             path: '/hr',               group: 'RECORDS', hideForHr: true },
  { id: '5c', icon: <ContactPhoneOutlinedIcon />,   label: 'Leads',             path: '/leads',            group: 'RECORDS', hideForHr: true },
  { id: '6',  icon: <AppsIcon />,                   label: 'Applications',      path: '/applications/list', group: 'RECORDS' },
  { id: '6b', icon: <PublicOutlinedIcon />,         label: 'External Jobs',     path: '/external-jobs',    group: 'RECORDS' },
  { id: '7',  icon: <SendOutlinedIcon />,           label: 'Email Campaigns',   path: '/email-campaigns/emails', group: 'OUTREACH', hideForHr: true },
  { id: '10b', icon: <MenuBookOutlinedIcon />,      label: 'Guides',            path: '/guides',           group: 'OUTREACH', hideForHr: true },
  { id: '10d', icon: <TravelExploreOutlinedIcon />, label: 'SEO, GEO, AEO',     path: '/seo',              group: 'OUTREACH', hideForHr: true },
  { id: '12', icon: <NotificationsActiveIcon />,    label: 'Free Job Alerts',   path: '/free-job-alerts',  group: 'OUTREACH', hideForHr: true },
  { id: '9',  icon: <SecurityIcon />,               label: 'User RBAC',         path: '/user-rbac',        group: 'ADMINISTRATION', hideForHr: true },
  { id: '10', icon: <LocalOfferOutlinedIcon />,     label: 'Settings',          path: '/job-metadata',     group: 'ADMINISTRATION', hideForHr: true },
  { id: '10c', icon: <GavelOutlinedIcon />,         label: 'Agreements',        path: '/agreements',       group: 'ADMINISTRATION', hideForHr: true },
  { id: '11', icon: <CreditCardIcon />,             label: 'Subscriptions',     path: '/subscriptions',    group: 'ADMINISTRATION', hideForHr: true },
  { id: '11b', icon: <DiscountOutlinedIcon />,      label: 'Promo codes',       path: '/promo-codes',      group: 'ADMINISTRATION', hideForHr: true },
  { id: '13', icon: <AttachMoneyIcon />,            label: 'Cloud & AI Costs',  path: '/cloud-costs/all',  group: 'ADMINISTRATION', hideForHr: true },
];

/**
 * The nav a role may see.
 *
 * The filter used to be written out twice, in the sidebar and the phone drawer, and a test asserted
 * the two source strings matched because that was the only way to catch them drifting. One
 * function removes the need for that, and is what let a third role be added at all: the flags are
 * booleans, so `hideForHr`/`hrOnly` cannot express three roles between them.
 *
 * Marketing is stated positively rather than by adding a `hideForMarketing` to every other entry.
 * Its scope is one destination, and the routes enforce it independently — `MarketingScope` in
 * LayoutPresentation — so this is presentation, not protection.
 */
export const visibleNavItems = (role?: string | null): NavigationItem[] => {
  if (isMarketingRole(role)) {
    // HR's view, without HR's own assigned clients (Marketing has none), plus Guides. Not the
    // Referrals page: it is your own referral link and where to pay you, which Marketing has no
    // part in; the programme's totals are on the dashboard's Referrals tab instead (owner, 2026-10-19).
    return NavigationItems.filter(
      (item) =>
        item.path.startsWith('/guides') ||
        item.path.startsWith('/seo') ||
        (!item.hideForHr && !item.hrOnly && item.path !== '/referrals'),
    );
  }
  if (isHrRole(role)) return NavigationItems.filter((item) => !item.hideForHr);
  if (isSuperAdmin(role)) return NavigationItems.filter((item) => !item.hrOnly);
  /*
   * An unrecognised role gets no navigation, rather than the Super Admin's.
   *
   * The old predicate was `!(hideForHr && isHr)`, so anything that was not HR fell through to the
   * full admin nav — the same fail-open shape as the role helpers. Safe to be strict here because
   * the authenticated layout only renders once the profile has loaded
   * (`isAuthenticated = status === 'succeeded'`), so this is never the still-loading case.
   */
  return [];
};

export default NavigationItems;
